const { useState: useStateL, useEffect: useEffectL, useRef: useRefL } = React;

function Landing({ tweaks, onEnterBuilder }) {
  return (
    <div
      style={{ ...landingStyles.page, "--mkt-pad": "max(24px, calc(8vw * var(--pad)))" }}
      data-screen-label="01 Landing"
    >
      {/* P653 — Consistency: pricing + templates already render
          ScrollProgress (thin accent bar pinned to top tracking page-
          scroll percentage). Landing is the longest marketing page
          (Hero + Marquee + HowItWorks + Showcase + FAQ + Footer)
          and benefitted least from no scroll affordance. The
          component is `aria-hidden="true"` so SR users get no
          duplicate announcement; sighted users get a visual hint
          for how far they've scrolled. */}
      <ScrollProgress />
      <MarketingNav active="home" />
      <Hero tweaks={tweaks} onEnterBuilder={onEnterBuilder} />
      <Marquee />
      <HowItWorks />
      <Showcase onEnterBuilder={onEnterBuilder} />
      <FAQ />
      <Footer />
    </div>
  );
}

// Kept for reference / hot-swap; no longer rendered. Remove once shared
// MarketingNav has had a bake-in.
// eslint-disable-next-line no-unused-vars
function LandingNav({ onLaunch }) {
  const auth = window.useAuth();
  const { t } = window.useT();
  const [scrolled, setScrolled] = useStateL(false);
  useEffectL(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav
      style={{
        ...landingStyles.nav,
        boxShadow: scrolled ? "0 8px 24px -16px rgba(0,0,0,0.18)" : "none",
        transition: "box-shadow 0.2s ease",
      }}
    >
      <div
        style={{
          ...landingStyles.navInner,
          padding: scrolled ? "10px max(24px, calc(8vw * var(--pad)))" : `16px max(24px, calc(8vw * var(--pad)))`,
          transition: "padding 0.2s ease",
        }}
      >
        <div style={landingStyles.brand}>
          <span style={landingStyles.brandMark} data-brand-mark aria-hidden="true">◆</span>
          <span style={landingStyles.brandWord}>Forge</span>
        </div>
        <div style={landingStyles.navLinks} data-mobile-hide>
          <a href="#how" data-link>{t("nav.how")}</a>
          <a href="#showcase" data-link>{t("nav.showcase")}</a>
          <a href="#faq" data-link>{t("nav.faq")}</a>
          <a
            href="/pricing"
            data-link
            onClick={window.spaNavOnClick("pricing")}
          >{t("nav.pricing")}</a>
        </div>
        <div style={landingStyles.navActions}>
          <LangSwitcher />
          {auth.isAuthed ? (
            <>
              <UserPill onDashboard={() => window.goTo("dashboard")} />
              <button type="button" style={landingStyles.btnSolid} onClick={() => window.goTo("dashboard")}>
                {t("nav.dashboard").replace(/\s*→\s*$/, "")} <span data-arrow aria-hidden="true">→</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" style={landingStyles.btnGhost} onClick={() => window.openAuth("sign-in")}>{t("nav.signIn")}</button>
              <button type="button" style={landingStyles.btnSolid} onClick={() => onLaunch()}>
                {t("nav.startBuilding")}
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

// P460 — placeholder examples resolve via t() at render so they
// follow the active locale. Held as keys; the rotator increments
// over the keys and the textarea reads the current localized value.
const HERO_PROMPT_KEYS = ["heroPrompt.0", "heroPrompt.1", "heroPrompt.2", "heroPrompt.3", "heroPrompt.4"];

function Hero({ tweaks, onEnterBuilder }) {
  const [draft, setDraft] = useStateL("");
  const { t } = window.useT();
  const [phIndex, setPhIndex] = useStateL(0);
  const placeholder = t(HERO_PROMPT_KEYS[phIndex]);
  // P215 — ref shared with chip clicks so the textarea picks up
  // focus + caret-at-end after a chip pre-fills, mirroring the
  // dashboard P214 fix.
  const taRef = useRefL(null);

  useEffectL(() => {
    if (draft.length > 0) return;
    // P258 — skip rotation when the tab is hidden.
    // P503 — also skip when the user has asked the OS to dial back
    // motion (vestibular-disorder accessibility). The CSS rule at
    // index.html:426 freezes CSS animations but JS-driven state
    // changes like this rotator weren't covered.
    if (typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setPhIndex((i) => (i + 1) % HERO_PROMPT_KEYS.length);
    }, 3200);
    return () => clearInterval(id);
  }, [draft]);

  const submit = () => {
    // P460 — fallback uses the localized placeholder rather than the
    // raw English seed (so an empty submit in RU sends the visible
    // RU prompt to the AI, not the English equivalent).
    onEnterBuilder(draft || placeholder);
  };

  return (
    <header style={landingStyles.hero}>
      <div style={landingStyles.heroEyebrow}>
        <span style={landingStyles.dot} />
        <span>{t("hero.eyebrow")}</span>
      </div>
      <h1 style={landingStyles.h1}>
        <span style={{ display: "block" }}>{t("hero.headlineL1")}</span>
        <span style={{ display: "block" }}>
          <em style={landingStyles.h1Em}>{t("hero.headlineL2")}</em>
        </span>
      </h1>
      <p style={landingStyles.lede}>{t("hero.lede")}</p>

      <div style={landingStyles.promptCard}>
        <div style={landingStyles.promptHeader}>
          <span style={landingStyles.promptLabel}>{t("hero.promptLabel")}</span>
          <span style={landingStyles.promptHint}>{t("hero.promptHint")}</span>
        </div>
        {/* P317 — auto-grow the hero textarea, mirroring the
            dashboard new-project fix (P316). Pre-fix `rows={3}`
            with overflow:auto on long briefs forced an internal
            scrollbar; the hero is the marketing surface where
            people compose their first prompt, so the cramped feel
            was a particularly bad first impression. Same 200px
            cap pattern. */}
        <textarea
          ref={taRef}
          style={{ ...landingStyles.promptInput, overflow: "auto", maxHeight: 200 }}
          value={draft}
          placeholder={placeholder}
          aria-label={t("hero.promptLabel")}
          onChange={(e) => setDraft(e.target.value)}
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = "auto";
            el.style.height = Math.min(el.scrollHeight, 200) + "px";
          }}
          onKeyDown={(e) => {
            // P544 — IME guard. See builder.jsx ChatComposer for
            // the full reasoning; same shape applied to the landing
            // hero Enter-to-submit.
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
              e.preventDefault();
              submit();
            }
          }}
          rows={3}
          maxLength={4000}
          // P592 — Mobile Enter key shows "Go" / "Send"; Enter
          // here submits the hero prompt (IME-guarded per P544).
          enterKeyHint="send"
        />
        <div style={landingStyles.promptFoot}>
          <div style={landingStyles.chips}>
            {/* P371 — split visible label (translated) from prompt
                seed (English). Same pattern as the dashboard chips
                (P354). Keeping the seed English so the AI brief is
                in the language it handles best, regardless of UI
                locale. */}
            {[
              { id: "landing",    labelKey: "landing.chipLanding",    seed: "a landing page for " },
              { id: "portfolio",  labelKey: "landing.chipPortfolio",  seed: "a portfolio for " },
              { id: "docs",       labelKey: "landing.chipDocs",       seed: "a docs site for " },
              { id: "invite",     labelKey: "landing.chipInvite",     seed: "an invite site for " },
              { id: "storefront", labelKey: "landing.chipStorefront", seed: "a storefront for " },
            ].map((c) => (
              <button
                type="button"
                key={c.id}
                style={landingStyles.chip}
                onClick={() => {
                  setDraft((d) => (d ? d : c.seed));
                  setTimeout(() => {
                    const el = taRef.current;
                    if (el) {
                      el.focus();
                      el.setSelectionRange(el.value.length, el.value.length);
                    }
                  }, 0);
                }}
              >
                {t(c.labelKey)}
              </button>
            ))}
          </div>
          <button type="button" style={landingStyles.heroCta} onClick={submit}>
            {t("hero.cta")} <span data-arrow aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <HeroPreview />
    </header>
  );
}

const _SPECIMENS = [
  "Aa Bb Cc Dd Ee Ff Gg",
  "Аа Бб Вв Гг Дд Ее Жж",
  "Αα Ββ Γγ Δδ Εε Ζζ Ηη",
  "Aa &y É j Ø Æ Œ ß",
];

function HeroPreview() {
  const [si, setSi] = useStateL(0);
  useEffectL(() => {
    // P258 — same visibility guard as the hero placeholder rotator.
    // P503 — also honor prefers-reduced-motion.
    if (typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setSi((i) => (i + 1) % _SPECIMENS.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={landingStyles.previewWrap} aria-hidden="true">
      <div style={landingStyles.previewLine} />
      <div style={landingStyles.previewWindow} data-float>
        <div style={landingStyles.previewChrome}>
          <span style={{ ...landingStyles.dotSm, background: "#FF5F57" }} />
          <span style={{ ...landingStyles.dotSm, background: "#FEBC2E" }} />
          <span
            style={{
              ...landingStyles.dotSm,
              background: "#28C840",
              boxShadow: "0 0 0 0 #28C840",
              animation: "pulse 2s ease-in-out infinite",
            }}
          />
          <div style={landingStyles.previewUrl}>
            <span style={landingStyles.lock}>⌁</span>
            forge.app/p/lisbon-type
          </div>
        </div>
        <div style={landingStyles.previewBody}>
          <div style={landingStyles.previewNav}>
            <span style={landingStyles.previewBrand}>Câmara Type</span>
            <span style={landingStyles.previewLinks}>
              <span>Specimens</span>
              <span>Custom</span>
              <span>About</span>
            </span>
          </div>
          <div style={landingStyles.previewHero}>
            <div style={landingStyles.previewKicker}>EST. 2021 · LISBON</div>
            <div style={landingStyles.previewH1}>
              Letterforms,<br />
              <em style={{ fontStyle: "italic", color: "var(--accent)" }}>
                drawn slowly.
              </em>
            </div>
            <div
            key={si}
            style={{ ...landingStyles.previewSpec, animation: "tab-fade 320ms ease-out both" }}
          >{_SPECIMENS[si]}</div>
          </div>
          <div style={landingStyles.previewGrid}>
            <div style={landingStyles.previewTile}>Esfera</div>
            <div style={landingStyles.previewTile}>Marítimo</div>
            <div style={landingStyles.previewTile}>Pedra</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Marquee() {
  // P453 — items localized.
  const { t } = window.useT();
  const items = [
    t("marquee.builtToday"),
    t("marquee.firstDeploy"),
    t("marquee.deploys"),
    t("marquee.stack"),
    t("marquee.editEnglish"),
    t("marquee.versions"),
  ];
  return (
    /* P520 — Marquee is purely decorative motion: the items duplicate
       (`[...items, ...items]`) for seamless infinite scroll, so SR
       users would hear each promotional string twice when navigating
       past it. The same product claims are covered in HowItWorks +
       Showcase + footer with single-instance text, so hiding the
       marquee from SR avoids the duplicate-announcement clutter. */
    <div
      style={landingStyles.marquee}
      aria-hidden="true"
      onMouseEnter={(e) => {
        const el = e.currentTarget.querySelector("[data-marquee-track]");
        if (el) el.style.animationPlayState = "paused";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget.querySelector("[data-marquee-track]");
        if (el) el.style.animationPlayState = "running";
      }}
    >
      <div style={landingStyles.marqueeTrack} data-marquee-track>
        {[...items, ...items].map((item, i) => (
          <span key={i} style={landingStyles.marqueeItem}>
            <span style={landingStyles.marqueeDot} data-hue-shift aria-hidden="true">●</span>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  // P447 — section localized.
  const { t } = window.useT();
  const steps = [
    { n: "01", title: t("how.step1Title"), body: t("how.step1Body"), art: <ArtPrompt /> },
    { n: "02", title: t("how.step2Title"), body: t("how.step2Body"), art: <ArtBuild /> },
    { n: "03", title: t("how.step3Title"), body: t("how.step3Body"), art: <ArtEdit /> },
    { n: "04", title: t("how.step4Title"), body: t("how.step4Body"), art: <ArtShip /> },
  ];
  return (
    <section id="how" style={landingStyles.section}>
      <SectionHead
        kicker={t("how.kicker")}
        title={<>{t("how.titleA")} <em style={landingStyles.titleEm}>{t("how.titleB")}</em></>}
      />
      <div style={landingStyles.steps}>
        {steps.map((s) => (
          <article key={s.n} style={landingStyles.stepCard}>
            <div style={landingStyles.stepNum}>{s.n}</div>
            {/* P465 — step-art is purely decorative imagery (file rows,
                pseudo-URLs, sample chat bubbles); aria-hidden the wrapper
                so SR doesn't announce the demo content as part of the
                step card. The step number + title + body still read. */}
            <div style={landingStyles.stepArt} aria-hidden="true">{s.art}</div>
            <h3 style={landingStyles.stepTitle}>{s.title}</h3>
            <p style={landingStyles.stepBody}>{s.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ArtPrompt() {
  return (
    <div style={artStyles.box}>
      <div style={{ ...artStyles.row, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>
        you ▸
      </div>
      <div style={artStyles.bubble}>
        a portfolio for a typeface designer in Lisbon, warm cream paper, italic display
      </div>
      <div style={artStyles.cursor} />
    </div>
  );
}

function ArtBuild() {
  return (
    <div style={artStyles.box}>
      <div style={artStyles.fileRow}>
        <span style={artStyles.fileDot} aria-hidden="true" />
        <span style={artStyles.fileName}>app/page.tsx</span>
        <span style={artStyles.fileMeta}>+128</span>
      </div>
      <div style={artStyles.fileRow}>
        <span style={artStyles.fileDot} aria-hidden="true" />
        <span style={artStyles.fileName}>components/Hero.tsx</span>
        <span style={artStyles.fileMeta}>+64</span>
      </div>
      <div style={artStyles.fileRow}>
        <span style={artStyles.fileDot} aria-hidden="true" />
        <span style={artStyles.fileName}>styles/tokens.css</span>
        <span style={artStyles.fileMeta}>+22</span>
      </div>
      <div style={artStyles.bar} />
    </div>
  );
}

function ArtEdit() {
  return (
    <div style={artStyles.box}>
      <div style={artStyles.bubble}>
        make the hero quieter, smaller type
      </div>
      <div style={{ ...artStyles.diff, color: "oklch(0.55 0.14 25)" }}>
        − text-7xl tracking-tight
      </div>
      <div style={{ ...artStyles.diff, color: "oklch(0.46 0.14 145)" }}>
        + text-5xl tracking-normal
      </div>
    </div>
  );
}

function ArtShip() {
  return (
    <div style={artStyles.box}>
      <div style={artStyles.shipUrl}>
        <span style={{ color: "var(--ink-3)" }}>https://</span>
        camara.type<span style={{ color: "var(--ink-3)" }}>/</span>
      </div>
      <div style={artStyles.shipPill}>
        <span style={artStyles.shipPulse} aria-hidden="true" />
        Live · 47s ago
      </div>
    </div>
  );
}

function Showcase({ onEnterBuilder }) {
  // P405 — section + per-card chrome localized via useT. Item titles
  // (Câmara Type / Northwell Sourdough / etc.) and tags
  // (PORTFOLIO / STOREFRONT) stay as-is — those are the demo's
  // sample brand names and content-tag enums, not UI chrome.
  const { t } = window.useT();
  const items = [
    { tag: "PORTFOLIO", title: "Câmara Type", swatch: "oklch(0.92 0.04 60)", ink: "oklch(0.25 0.05 30)" },
    { tag: "STOREFRONT", title: "Northwell Sourdough", swatch: "oklch(0.88 0.06 80)", ink: "oklch(0.3 0.08 50)" },
    { tag: "DOCS", title: "Ratelimit.dev", swatch: "oklch(0.18 0.01 250)", ink: "oklch(0.92 0.03 90)" },
    { tag: "LANDING", title: "Atlas Scheduler", swatch: "oklch(0.96 0.02 240)", ink: "oklch(0.3 0.05 250)" },
    { tag: "INVITE", title: "Mira & Theo", swatch: "oklch(0.94 0.03 30)", ink: "oklch(0.35 0.08 20)" },
    { tag: "PORTFOLIO", title: "Studio Halfmoon", swatch: "oklch(0.22 0.02 140)", ink: "oklch(0.94 0.05 90)" },
  ];
  return (
    <section id="showcase" style={landingStyles.section}>
      <SectionHead
        kicker={t("showcase.kicker")}
        title={t("showcase.title")}
        right={
          <a
            href="/templates"
            style={landingStyles.linkArrow}
            onClick={window.spaNavOnClick("templates")}
          >
            {t("showcase.seeAll")}
          </a>
        }
      />
      <div style={landingStyles.gallery} data-grid-dim>
        {items.map((it, i) => {
          const open = () => window.toast(t("showcase.openToast").replace("{title}", it.title));
          // P181 — same keyboard-activation fix as P180. <article> with
          // onClick wasn't focusable or Enter/Space-activatable, so the
          // showcase grid was visible-only for keyboard users.
          const onKey = (e) => {
            if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
              e.preventDefault();
              open();
            }
          };
          return (
          <article
            key={i}
            data-card
            style={{
              ...landingStyles.galleryCard,
              background: it.swatch,
              color: it.ink,
            }}
            onClick={open}
            onKeyDown={onKey}
            tabIndex={0}
            role="button"
            aria-label={t("showcase.openAria").replace("{title}", it.title)}
          >
            <div style={landingStyles.galleryTag}>{it.tag}</div>
            <div style={landingStyles.galleryTitle}>{it.title}</div>
            <div style={landingStyles.galleryFoot}>
              <span style={landingStyles.galleryDot} aria-hidden="true" />
              <span style={landingStyles.galleryMeta}>{t("showcase.builtIn")}</span>
            </div>
            <span data-reveal style={landingStyles.galleryRevealCta} aria-hidden="true">{t("showcase.openCta")} →</span>
          </article>
          );
        })}
      </div>
      <div style={landingStyles.bigCta}>
        <h2 style={landingStyles.bigCtaTitle}>
          {t("showcase.bigCtaTitle")}
        </h2>
        <button type="button" style={landingStyles.btnSolidBig} onClick={onEnterBuilder}>
          {t("showcase.bigCta")}
        </button>
      </div>
    </section>
  );
}

function FAQ() {
  // P404 — Q+A localized via useT.
  const { t } = window.useT();
  const items = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
  ];
  const [open, setOpen] = useStateL(0);
  return (
    <section id="faq" style={landingStyles.section}>
      <SectionHead
        kicker={t("faq.kicker")}
        title={<>{t("faq.titleA")} <em style={landingStyles.titleEm}>{t("faq.titleB")}</em></>}
      />
      <div style={landingStyles.faq}>
        {/* P555 — Same fix as pricing.jsx P554. The answer used to
            be a child of the disclosure-trigger button (HTML-spec
            illegal interactive-content nesting + clicking the
            answer text toggled the disclosure). Restructured to the
            standard disclosure pattern: <div> row with <button>
            + sibling answer container, wired via aria-controls /
            aria-labelledby. */}
        {items.map((it, i) => {
          const isOpen = open === i;
          const btnId = `landing-faq-q-${i}`;
          const ansId = `landing-faq-a-${i}`;
          return (
            <div key={i} style={landingStyles.faqRow}>
              <button
                type="button"
                id={btnId}
                style={landingStyles.faqQ}
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                aria-controls={ansId}
              >
                <span>{it.q}</span>
                <span style={{ ...landingStyles.faqIcon, transform: isOpen ? "rotate(45deg)" : "rotate(0)" }} aria-hidden="true">+</span>
              </button>
              {isOpen && (
                <div
                  id={ansId}
                  role="region"
                  aria-labelledby={btnId}
                  style={landingStyles.faqA}
                >{it.a}</div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Footer() {
  // P372 — link labels + status pill + copyright localized via useT.
  const { t } = window.useT();
  const stub = (label) => (e) => {
    e.preventDefault();
    window.toast(t("card.menu.comingSoon").replace("{action}", label));
  };
  const FLink = ({ to, label }) => (
    <a href={to || "#"} onClick={to ? undefined : stub(label)}>{label}</a>
  );
  return (
    <footer style={landingStyles.footer}>
      <div style={landingStyles.footerTop}>
        <div style={landingStyles.footerBrand}>
          <span style={landingStyles.brandMark} data-brand-mark aria-hidden="true">◆</span>
          <span style={landingStyles.footerWord}>Forge</span>
        </div>
        <div style={landingStyles.footerCols}>
          <div style={landingStyles.footerCol}>
            <div style={landingStyles.footerHead}>{t("footer.product")}</div>
            <FLink to="#how" label={t("footer.builder")} />
            <FLink to="#showcase" label={t("footer.showcase")} />
            <a
              href="/pricing"
              onClick={window.spaNavOnClick("pricing")}
            >{t("nav.pricing")}</a>
            <FLink label={t("footer.changelog")} />
          </div>
          <div style={landingStyles.footerCol}>
            <div style={landingStyles.footerHead}>{t("footer.company")}</div>
            <FLink label={t("footer.about")} />
            <FLink label={t("footer.blog")} />
            <FLink label={t("footer.careers")} />
            <FLink label={t("footer.press")} />
          </div>
          <div style={landingStyles.footerCol}>
            <div style={landingStyles.footerHead}>{t("footer.resources")}</div>
            <FLink label={t("footer.docs")} />
            <a
              href="/templates"
              onClick={window.spaNavOnClick("templates")}
            >{t("nav.templates")}</a>
            <a
              href="/privacy"
              onClick={window.spaNavOnClick("privacy")}
            >{t("footer.privacy")}</a>
            <a
              href="/terms"
              onClick={window.spaNavOnClick("terms")}
            >{t("footer.terms")}</a>
          </div>
        </div>
      </div>
      <div style={landingStyles.footerBottom}>
        <span style={landingStyles.footerBottomLeft}>
          {t("footer.copyright")}
          {/* P500 — was <a href="#" onClick={toast}>, which SR users
              announced as "link, All systems normal" — misleading,
              since clicking doesn't navigate (it just shows a status
              toast). Now a real <button>; SR announces the action
              correctly + middle-click no longer opens about:blank. */}
          <button
           type="button"
            onClick={() => window.toast(t("footer.allSystemsToast"))}
            style={landingStyles.statusPill}
            // P547 — title= mirrors data-tooltip ("API · Edge · Auth")
            // for AT readability — visible "All systems normal" alone
            // doesn't expose the per-component breakdown to SR users.
            data-tooltip={t("footer.allSystemsTooltip")}
            title={t("footer.allSystemsTooltip")}
            data-tooltip-side="top"
          >
            <span style={landingStyles.statusDot} aria-hidden="true" />
            {t("footer.allSystemsNormal")}
          </button>
        </span>
        <span style={landingStyles.footerBig} data-drift-bg>Forge</span>
        <span>{t("footer.madeWith")}</span>
      </div>
    </footer>
  );
}

function SectionHead({ kicker, title, right }) {
  return (
    <div style={landingStyles.sectionHead}>
      <div style={landingStyles.sectionLeft}>
        <div style={landingStyles.kicker}>{kicker}</div>
        <h2 style={landingStyles.h2}>{title}</h2>
      </div>
      {right && <div style={landingStyles.sectionRight}>{right}</div>}
    </div>
  );
}

const PAD = "max(24px, calc(8vw * var(--pad)))";

const landingStyles = {
  page: {
    background: "var(--paper)",
    color: "var(--ink)",
    fontFamily: "var(--font-ui)",
    minHeight: "100vh",
  },
  nav: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "color-mix(in oklab, var(--paper) 88%, transparent)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: "1px solid var(--rule)",
  },
  navInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    padding: `16px ${PAD}`,
  },
  brand: { display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--font-display)" },
  brandMark: { color: "var(--accent)", fontSize: 14 },
  brandWord: { fontSize: 22, letterSpacing: "-0.01em" },
  navLinks: { display: "flex", gap: 28, fontSize: 14, color: "var(--ink-2)" },
  navActions: { display: "flex", gap: 8, alignItems: "center" },
  btnGhost: {
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: 14,
    color: "var(--ink-2)",
    whiteSpace: "nowrap",
  },
  btnSolid: {
    padding: "8px 16px",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 500,
    background: "var(--ink)",
    color: "var(--paper)",
    whiteSpace: "nowrap",
  },

  hero: {
    padding: `clamp(56px, 9vw, 120px) ${PAD} 32px`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    position: "relative",
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    borderRadius: 999,
    border: "1px solid var(--rule)",
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    color: "var(--ink-2)",
    marginBottom: 28,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "var(--accent)",
    boxShadow: "0 0 0 4px color-mix(in oklab, var(--accent) 25%, transparent)",
    animation: "pulse 2s ease-in-out infinite",
  },
  h1: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: "clamp(56px, 9vw, 132px)",
    lineHeight: 1.05,
    letterSpacing: "-0.025em",
    maxWidth: 1100,
    color: "var(--ink)",
  },
  h1Em: {
    fontStyle: "italic",
    color: "var(--accent)",
  },
  lede: {
    fontSize: "clamp(17px, 1.6vw, 21px)",
    color: "var(--ink-2)",
    maxWidth: 620,
    lineHeight: 1.4,
    marginTop: 24,
    textWrap: "pretty",
  },

  promptCard: {
    marginTop: 44,
    width: "min(720px, 100%)",
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 1px 0 rgba(255,255,255,0.5) inset, 0 24px 60px -28px rgba(0,0,0,0.18)",
    textAlign: "left",
  },
  promptHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 6px 10px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
  },
  promptLabel: { color: "var(--accent)", letterSpacing: "0.08em" },
  promptHint: { color: "var(--ink-3)" },
  promptInput: {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "var(--ink)",
    fontSize: 18,
    fontFamily: "var(--font-ui)",
    lineHeight: 1.45,
    resize: "none",
    padding: "8px 6px",
  },
  promptFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 12,
    borderTop: "1px dashed var(--rule)",
    flexWrap: "wrap",
  },
  chips: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: {
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid var(--rule)",
    fontSize: 12,
    color: "var(--ink-2)",
    background: "var(--paper)",
  },
  heroCta: {
    padding: "10px 20px",
    borderRadius: 999,
    background: "var(--accent)",
    color: "var(--accent-fg)",
    fontWeight: 500,
    fontSize: 14,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },

  previewWrap: {
    marginTop: 64,
    width: "min(1100px, 100%)",
    position: "relative",
    paddingTop: 28,
  },
  previewLine: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: 1,
    height: 28,
    background: "linear-gradient(to bottom, transparent, var(--rule))",
  },
  previewWindow: {
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 40px 100px -30px rgba(0,0,0,0.25)",
  },
  previewChrome: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "12px 16px",
    background: "var(--paper-3)",
    borderBottom: "1px solid var(--rule)",
  },
  dotSm: { width: 11, height: 11, borderRadius: 999, display: "inline-block" },
  previewUrl: {
    flex: 1,
    textAlign: "center",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--ink-3)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
  },
  lock: { color: "var(--accent)" },
  previewBody: {
    padding: "32px clamp(24px, 5vw, 56px) 48px",
    background: "oklch(0.97 0.012 75)",
    color: "oklch(0.22 0.02 40)",
    minHeight: 380,
  },
  previewNav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 36,
    fontFamily: "var(--font-display)",
  },
  previewBrand: { fontSize: 22, letterSpacing: "-0.01em" },
  previewLinks: {
    display: "flex",
    gap: 24,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "oklch(0.45 0.02 40)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  previewHero: { textAlign: "left", marginBottom: 32 },
  previewKicker: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.1em",
    color: "oklch(0.45 0.02 40)",
    marginBottom: 16,
  },
  previewH1: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(40px, 6vw, 88px)",
    lineHeight: 1.05,
    letterSpacing: "-0.02em",
  },
  previewSpec: {
    fontFamily: "var(--font-display)",
    fontSize: 36,
    color: "oklch(0.55 0.04 40)",
    marginTop: 16,
    letterSpacing: "0.02em",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 16,
  },
  previewTile: {
    aspectRatio: "4 / 3",
    background: "oklch(0.93 0.03 60)",
    borderRadius: 6,
    display: "flex",
    alignItems: "flex-end",
    padding: 12,
    fontFamily: "var(--font-display)",
    fontSize: 22,
    color: "oklch(0.3 0.04 40)",
  },

  marquee: {
    borderTop: "1px solid var(--rule)",
    borderBottom: "1px solid var(--rule)",
    overflow: "hidden",
    padding: "18px 0",
    marginTop: 80,
    background: "var(--paper-2)",
  },
  marqueeTrack: {
    display: "flex",
    gap: 48,
    whiteSpace: "nowrap",
    animation: "scroll 38s linear infinite",
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--ink-2)",
  },
  marqueeItem: { display: "inline-flex", alignItems: "center", gap: 12 },
  marqueeDot: { color: "var(--accent)", fontSize: 8 },

  section: {
    padding: `clamp(80px, 10vw, 140px) ${PAD}`,
    borderBottom: "1px solid var(--rule)",
  },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 56,
    gap: 24,
    flexWrap: "wrap",
  },
  sectionLeft: { maxWidth: 720 },
  sectionRight: {},
  kicker: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    letterSpacing: "0.1em",
    color: "var(--accent)",
    marginBottom: 16,
  },
  h2: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: "clamp(40px, 5.5vw, 72px)",
    lineHeight: 1.08,
    letterSpacing: "-0.02em",
  },
  titleEm: { fontStyle: "italic", color: "var(--accent)" },
  linkArrow: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--ink-2)",
    borderBottom: "1px solid var(--rule)",
    paddingBottom: 4,
  },

  steps: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 1,
    background: "var(--rule)",
    border: "1px solid var(--rule)",
  },
  stepCard: {
    background: "var(--paper)",
    padding: 28,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minHeight: 360,
  },
  stepNum: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--ink-3)",
    letterSpacing: "0.1em",
  },
  stepArt: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0" },
  stepTitle: {
    fontFamily: "var(--font-display)",
    fontSize: 28,
    lineHeight: 1.05,
    letterSpacing: "-0.01em",
    fontWeight: 400,
  },
  stepBody: { color: "var(--ink-2)", fontSize: 14, lineHeight: 1.5, textWrap: "pretty" },

  gallery: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
  },
  galleryCard: {
    aspectRatio: "4 / 3",
    borderRadius: 12,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "transform 0.2s ease",
    fontFamily: "var(--font-display)",
    position: "relative",
  },
  galleryTag: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    letterSpacing: "0.12em",
    opacity: 0.7,
  },
  galleryTitle: {
    fontSize: 36,
    lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  galleryFoot: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    opacity: 0.6,
  },
  galleryRevealCta: {
    position: "absolute",
    right: 14,
    bottom: 14,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "currentColor",
    background: "color-mix(in oklab, currentColor 12%, transparent)",
    padding: "3px 10px",
    border: "1px solid color-mix(in oklab, currentColor 30%, transparent)",
    borderRadius: 999,
    pointerEvents: "none",
  },
  galleryDot: { width: 6, height: 6, borderRadius: 999, background: "currentColor" },
  galleryMeta: {},

  bigCta: {
    marginTop: 96,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 32,
  },
  bigCtaTitle: {
    fontFamily: "var(--font-display)",
    fontWeight: 400,
    fontSize: "clamp(48px, 7vw, 96px)",
    lineHeight: 1.05,
    letterSpacing: "-0.025em",
  },
  btnSolidBig: {
    padding: "16px 32px",
    borderRadius: 999,
    background: "var(--ink)",
    color: "var(--paper)",
    fontSize: 16,
    fontWeight: 500,
  },

  faq: {
    display: "flex",
    flexDirection: "column",
    borderTop: "1px solid var(--rule)",
    width: "min(900px, 100%)",
    margin: "0 auto",
  },
  // P555 — faqRow is a <div> now (P555), not a <button>. Removed
  // button-only reset bits (background:transparent, cursor:pointer
  // — the inner button gets cursor from index.html's global rule).
  faqRow: {
    padding: "28px 4px",
    borderBottom: "1px solid var(--rule)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  faqQ: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "var(--font-display)",
    fontSize: "clamp(20px, 2.4vw, 28px)",
    lineHeight: 1.2,
    // P555 — explicit text-align since this is now a button (which
    // doesn't pin alignment by default).
    textAlign: "left",
    width: "100%",
  },
  faqIcon: {
    fontSize: 24,
    color: "var(--accent)",
    transition: "transform 0.2s ease",
    fontFamily: "var(--font-ui)",
    fontWeight: 300,
  },
  faqA: {
    color: "var(--ink-2)",
    fontSize: 16,
    lineHeight: 1.5,
    maxWidth: 720,
    textWrap: "pretty",
  },

  footer: {
    background: "var(--paper-2)",
    padding: `64px ${PAD} 24px`,
  },
  footerTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 48,
    flexWrap: "wrap",
    borderBottom: "1px solid var(--rule)",
    paddingBottom: 48,
  },
  footerBrand: { display: "flex", alignItems: "center", gap: 10, fontFamily: "var(--font-display)", fontSize: 28 },
  footerWord: { letterSpacing: "-0.01em" },
  footerCols: { display: "flex", gap: 64, flexWrap: "wrap" },
  footerCol: { display: "flex", flexDirection: "column", gap: 10, fontSize: 14, color: "var(--ink-2)" },
  footerHead: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.1em",
    color: "var(--ink-3)",
    marginBottom: 6,
  },
  footerBottom: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    paddingTop: 24,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--ink-3)",
  },
  footerBig: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(44px, 8vw, 120px)",
    lineHeight: 1,
    color: "color-mix(in oklab, var(--ink) 34%, transparent)",
    letterSpacing: "-0.04em",
    textAlign: "center",
    fontStyle: "italic",
  },
  footerBottomLeft: {
    display: "inline-flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    border: "1px solid var(--rule)",
    borderRadius: 999,
    color: "var(--ink-2)",
    fontFamily: "var(--font-mono)",
    fontSize: 10.5,
    letterSpacing: "0.04em",
    textDecoration: "none",
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "oklch(0.6 0.16 145)",
    boxShadow: "0 0 0 3px color-mix(in oklab, oklch(0.6 0.16 145) 22%, transparent)",
    animation: "pulse 2.2s ease-in-out infinite",
  },
};

const artStyles = {
  box: {
    width: "100%",
    height: "100%",
    minHeight: 160,
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    fontSize: 12,
  },
  row: { display: "flex", alignItems: "center", gap: 8 },
  bubble: {
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 12,
    color: "var(--ink-2)",
    lineHeight: 1.4,
  },
  cursor: {
    width: 7,
    height: 14,
    background: "var(--accent)",
    alignSelf: "flex-start",
    animation: "blink 1s steps(2, start) infinite",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
  },
  fileDot: { width: 6, height: 6, borderRadius: 999, background: "var(--accent)" },
  fileName: { color: "var(--ink-2)", flex: 1 },
  fileMeta: { color: "oklch(0.46 0.14 145)", fontSize: 10 },
  bar: {
    marginTop: 8,
    height: 4,
    borderRadius: 4,
    background: "linear-gradient(to right, var(--accent) 70%, var(--rule) 70%)",
  },
  diff: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    padding: "4px 8px",
    borderRadius: 4,
    background: "var(--paper)",
  },
  shipUrl: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    padding: "8px 12px",
    background: "var(--paper)",
    border: "1px solid var(--rule)",
    borderRadius: 8,
    color: "var(--ink)",
  },
  shipPill: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    background: "color-mix(in oklab, var(--accent) 15%, var(--paper))",
    // accent-hover is the same hue at 0.5 lightness — clears WCAG AA
    // (4.5:1) on the tinted-accent chip background; bare --accent
    // (0.56 lightness) used to land at 3.89.
    color: "var(--accent-hover)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
  },
  shipPulse: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "var(--accent)",
    boxShadow: "0 0 0 3px color-mix(in oklab, var(--accent) 30%, transparent)",
    animation: "pulse 1.6s ease-in-out infinite",
  },
};

// inject keyframes once
if (!document.getElementById("__landing_keyframes")) {
  const style = document.createElement("style");
  style.id = "__landing_keyframes";
  style.textContent = `
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
    @keyframes blink { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0; } }
    @keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
  `;
  document.head.appendChild(style);
}

window.Landing = Landing;
